const FAQ_NAV_SELECTOR = '[data-faq-ma-nav]';
const FAQ_LINK_SELECTOR = '[data-faq-ma-nav-link][href^="#"]';
const FAQ_SELECT_SELECTOR = '.faq-ma-nav-select select';
const ACTIVE_CLASS = 'opacity-100';
const INACTIVE_CLASS = 'opacity-35';

class FaqMaNav extends HTMLElement {
  constructor() {
    super();
    this.frame = null;
    this.updateOnFrame = this.updateOnFrame.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
  }

  connectedCallback() {
    this.nav = this.querySelector(FAQ_NAV_SELECTOR);
    this.links = Array.from(this.querySelectorAll(FAQ_LINK_SELECTOR));
    this.selectElement = this.querySelector('.faq-ma-nav-select input-select');
    this.select = this.querySelector(FAQ_SELECT_SELECTOR);

    if (!this.links.length) {
      return;
    }

    this.update();

    document.addEventListener('scroll', this.updateOnFrame, { passive: true });
    window.addEventListener('resize', this.updateOnFrame);
    window.addEventListener('hashchange', this.updateOnFrame);
    document.addEventListener('waka:morph:done', this.updateOnFrame);

    if (this.select) {
      this.select.addEventListener('change', this.handleSelectChange);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.updateOnFrame);
    window.removeEventListener('resize', this.updateOnFrame);
    window.removeEventListener('hashchange', this.updateOnFrame);
    document.removeEventListener('waka:morph:done', this.updateOnFrame);

    if (this.select) {
      this.select.removeEventListener('change', this.handleSelectChange);
    }
  }

  getTargetFromHash(hash) {
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!id) return null;

    const decodedId = decodeURIComponent(id);
    const escapedId = CSS.escape(decodedId);

    return (
      document.getElementById(decodedId) ||
      document.querySelector(`[name="${escapedId}"]`)
    );
  }

  getTargetFromLink(link) {
    const href = link.getAttribute('href') || '';
    if (!href.startsWith('#') || href.length <= 1) return null;

    return this.getTargetFromHash(href);
  }

  scrollToHash(hash) {
    if (!hash) return;

    const target = this.getTargetFromHash(hash);
    if (!target) return;

    const headerHeight = window.getHeaderHeight();
    const additionalOffset = window.innerWidth >= 1024 ? 30 : 20;
    const top =
      target.getBoundingClientRect().top +
      window.scrollY -
      (headerHeight + additionalOffset);

    window.scrollTo({ top, behavior: 'smooth' });
  }

  setActiveState(activeLink) {
    this.links.forEach((link) => {
      const isActive = link === activeLink;
      link.classList.toggle(ACTIVE_CLASS, isActive);
      link.classList.toggle(INACTIVE_CLASS, !isActive);
    });
  }

  resolveActiveLink() {
    const thresholdTop = window.getHeaderHeight() + 1;
    const viewportHeight = window.innerHeight;
    let fallback = null;

    this.links.forEach((link) => {
      if (fallback) return;

      const target = this.getTargetFromLink(link);
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const fullyBehindHeader = rect.bottom <= thresholdTop;
      const visibleInViewport = rect.top < viewportHeight;

      if (!fullyBehindHeader && visibleInViewport) {
        fallback = link;
      }
    });

    return fallback;
  }

  update() {
    const activeLink = this.resolveActiveLink();
    this.setActiveState(activeLink);

    const activeHash = activeLink?.getAttribute('href');
    if (!activeHash || !activeHash.startsWith('#')) return;

    if (
      this.selectElement &&
      typeof this.selectElement.setValue === 'function'
    ) {
      this.selectElement.setValue(activeHash);
      return;
    }

    if (this.select) {
      this.select.value = activeHash;
    }
  }

  updateOnFrame() {
    if (this.frame !== null) return;

    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  handleSelectChange(event) {
    const hash = event.target?.value;
    if (!hash || !hash.startsWith('#')) return;

    history.pushState(null, '', hash);
    this.scrollToHash(hash);
    this.updateOnFrame();
  }
}

if (!window.customElements.get('faq-ma-nav')) {
  window.customElements.define('faq-ma-nav', FaqMaNav);
}
